import { useMemo } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { usePracticeAnalyticsShallowStore } from './use-practice-analytics-store';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      newPatients {
        historicalData(start: $start, end: $end, step: $step) {
          label
          timestamp
          total
        }
        totals(start: $start, end: $end, step: $step) {
          patients
          production
          productionPerDay {
            date
            amount
          }
          unscheduledProduction
          unscheduledProductionPerDay {
            date
            amount
          }
        }
      }
    }
  }
`;

export const useGetNewPatientsMetric = () => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');

  const { demoData, filters, isDemoAccount } = usePracticeAnalyticsShallowStore('demoData', 'filters', 'isDemoAccount');

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(`newPatientsSummary-${isDemoAccount}-${JSON.stringify(filters)}`),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.NewPatientsTrend>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.newPatients : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { aggregatedData, rawData } = useMemo(() => {
    return PracticeAnalyticsAggregations.newPatientsSummary(data);
  }, [data]);

  return {
    aggregatedNewPatientsData: aggregatedData,
    isLoading,
    rawNewPatientsData: rawData,
  };
};
